import { desktopBridge, withDesktop } from '@/features/desktop/bridge';
import type { DesktopNotificationKind } from '@/features/desktop/types';
import type { NotificationItem, NotificationKind } from './types';

/**
 * Banners currently on screen, by notification id.
 *
 * Only the ones the OS will NOT retire on its own need tracking — see the note
 * next to `requireInteraction`. Entries are dropped on close and on click, so
 * this cannot grow without bound.
 */
/**
 * Fallback handles only — see `raise` for why this is nearly always empty.
 *
 * A notification created with `new Notification()` belongs to the PAGE. It is
 * invisible to `ServiceWorkerRegistration.getNotifications()`, which by
 * specification returns only what the worker itself showed. That is the whole
 * reason the earlier retraction failed: the sweep in sw.js was structurally
 * blind to every banner this file had raised, so day-old call notifications sat
 * in the OS shade with nothing able to reach them. Anything raised through the
 * worker is retrievable and closable from either side; only the no-worker
 * fallback still needs a local handle.
 */
const live = new Map<string, { banner: Notification; scopeId: string | null }>();

/** The registration, when this browser has one and it is ready. */
async function workerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

/**
 * Retract every banner raised about one thing — a call, a conversation.
 *
 * Keyed on the SCOPE, because the caller knows which call just ended and never
 * the uuid of the row that announced it. One ring can raise more than one
 * banner (a re-ring, a second device) and they all have to go together.
 */
export function dismissDesktopNotificationsForScope(scopeId: string): void {
  /* THE ELECTRON SHELL WAS NOT IN THIS FUNCTION AT ALL.
     `showDesktopNotification` routes through `bridge.notify` when the shell is
     present and returns; this function then swept only page-owned and
     service-worker banners, neither of which exists there. So the one exit
     every ring passes through could not retract the one banner the desktop
     client had actually raised. `clearCall` closes both the native ringer
     window and the toast slot, keyed `call:<id>` in main/ipc.ts, and is
     idempotent — it is already called from useDesktopCalls on the same
     transition. */
  withDesktop((bridge) => bridge.clearCall(scopeId));

  /* Page-owned fallback banners. */
  for (const [id, entry] of [...live.entries()]) {
    if (entry.scopeId !== scopeId) continue;
    live.delete(id);
    try {
      entry.banner.close();
    } catch {
      /* Already gone, or the platform revoked it. */
    }
  }

  /* Worker-owned banners — the normal case. */
  void workerRegistration()
    .then(async (registration) => {
      if (registration === null) return;
      const shown = await registration.getNotifications();
      for (const notification of shown) {
        const data: unknown = notification.data;
        const scope =
          typeof data === 'object' && data !== null
            ? (data as { scopeId?: unknown }).scopeId
            : undefined;
        if (scope === scopeId) notification.close();
      }
    })
    .catch(() => undefined);
}

/**
 * Take back ONE banner, by notification id — because it has been read, or
 * because the server withdrew the row behind it.
 *
 * On Windows this genuinely retracts: Electron 33 implements `close()` as
 * `IToastNotificationHistory::RemoveGroupedTagWithId`, so the toast leaves the
 * Action Center and not merely the screen. Without this, a message you had
 * already read went on sitting in the shade until something else replaced it —
 * the same class of defect as the ten-day-old probe toasts.
 *
 * Scope-wide retraction (a call that ended, every banner about it) stays in
 * `dismissDesktopNotificationsForScope`; this is the single-row case.
 */
export function dismissDesktopNotification(id: string): void {
  withDesktop((bridge) => bridge.closeNotification(id));

  /* Page-owned fallback banner, when this browser has no service worker. */
  const entry = live.get(id);
  if (entry !== undefined) {
    live.delete(id);
    try {
      entry.banner.close();
    } catch {
      /* Already gone, or the platform revoked it. */
    }
  }

  /* Worker-owned banners — the normal browser case. Matched on `data.id`,
     which is why `showDesktopNotification` now carries it. */
  void workerRegistration()
    .then(async (registration) => {
      if (registration === null) return;
      for (const shown of await registration.getNotifications()) {
        const data: unknown = shown.data;
        const shownId =
          typeof data === 'object' && data !== null ? (data as { id?: unknown }).id : undefined;
        if (shownId === id) shown.close();
      }
    })
    .catch(() => undefined);
}

/** Wrapper around the Notification API that degrades silently when unavailable. */
export function desktopSupported(): boolean {
  if (desktopBridge() !== null) return true;
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function desktopPermission(): NotificationPermission {
  /* The shell owns the OS-level permission, and there is nothing for the page
     to prompt for — an installed application is already trusted to notify. */
  if (desktopBridge() !== null) return 'granted';
  return desktopSupported() ? Notification.permission : 'denied';
}

/** Must be called from a user gesture — browsers reject unprompted requests. */
export async function requestDesktopPermission(): Promise<NotificationPermission> {
  if (desktopBridge() !== null) return 'granted';
  if (!desktopSupported()) return 'denied';
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}

export interface DesktopOptions {
  showPreview: boolean;
  onActivate: (item: NotificationItem) => void;
}

/**
 * Kinds the SHELL already answers with a better surface of its own, and which
 * must therefore never also become a toast.
 *
 * An incoming call raises the always-on-top ringer window — 380x216, with
 * Answer and Decline on it, over full-screen apps, working while the workspace
 * is hidden in the tray. It was ALSO raising a Windows toast (from the shell's
 * own `IPC.ringCall`) and a second one from here, on top of the in-app dark
 * card, so one ring produced up to four surfaces. The ringer is the only one
 * that can actually answer, so it wins outright. A MISSED call still toasts:
 * it has no other surface and nothing to answer.
 */
const SHELL_HANDLES_ELSEWHERE: ReadonlySet<NotificationKind> = new Set(['callIncoming']);

/**
 * The workspace and account the toast puts in its attribution line — the one
 * piece of context a notification cannot get from the message itself, and the
 * only reason an app name belongs on one at all.
 *
 * Held here as plain module state because it is read from `showDesktopNotification`,
 * which is not a component and must not become one: notifications arrive on a
 * socket, outside React, and a burst must cost no renders. `NotificationRuntime`
 * pushes it in whenever the session changes.
 */
let attribution: { workspace: string; account: string } = { workspace: '', account: '' };

export function setDesktopAttribution(next: { workspace: string; account: string }): void {
  attribution = next;
}

/**
 * True when the user is already reading THIS conversation, in a focused window.
 *
 * Teams does not interrupt you about the thread you are looking at, and neither
 * should this — but the old rule was the opposite and far blunter: it suppressed
 * the system toast whenever the app had focus AT ALL, which is precisely why the
 * only popup he ever saw while working in the app was the in-app card he
 * complained about. Focus alone is not the question. The conversation is.
 */
function alreadyReadingIt(item: NotificationItem): boolean {
  if (typeof document === 'undefined' || !document.hasFocus()) return false;
  const link = item.deepLink;
  if (link === undefined || link.length === 0) return false;
  const route = link.split('?')[0] ?? '';
  return route.length > 0 && window.location.pathname === route;
}

/** Which of the three OS treatments a notification kind deserves. */
const DESKTOP_KIND: Readonly<Partial<Record<NotificationKind, DesktopNotificationKind>>> = {
  mention: 'mention',
  reply: 'mention',
  message: 'dm',
  callIncoming: 'call',
  callMissed: 'call',
  voicemail: 'call',
};

/**
 * Shows a native banner. `tag` reuses the OS slot for the same conversation so
 * a burst of replies collapses into one banner instead of stacking ten.
 *
 * In the desktop shell this goes through the main process, which can attach
 * real buttons and an inline reply field where the platform allows it, and can
 * raise a banner while the window is closed to the tray — neither of which the
 * page's own Notification API can do.
 */
export function showDesktopNotification(item: NotificationItem, options: DesktopOptions): void {
  const bridge = desktopBridge();
  if (bridge !== null) {
    /*
     * THE FOCUS GATE IS GONE, AND ITS REMOVAL IS THE FIX.
     *
     * This used to `return` whenever the window was visible and focused, on the
     * reasoning that a banner is noise when you are already looking at the app.
     * The effect was the exact inverse of what was asked for: with the app in
     * front the system popup was suppressed and the IN-APP card was the only
     * thing left — "rather its showing inside teams app" — and with the app
     * merely unfocused BOTH appeared, which is "coming seperately in popups".
     *
     * On the desktop the system toast is now the only transient surface there
     * is, so it fires whether or not the window has focus. The one case worth
     * suppressing is much narrower and is handled by `alreadyReadingIt`.
     */
    if (SHELL_HANDLES_ELSEWHERE.has(item.kind)) return;
    if (alreadyReadingIt(item)) return;
    void bridge
      .notify({
        id: item.id,
        kind: DESKTOP_KIND[item.kind] ?? 'other',
        title: item.title,
        body: options.showPreview ? (item.preview.snippet ?? item.body ?? '') : 'New activity',
        ...(item.deepLink === undefined ? {} : { deepLink: item.deepLink }),
        ...(item.scopeKind === 'conversation' && item.scopeId !== undefined
          ? { conversationId: item.scopeId }
          : {}),
        ...(item.groupKey === undefined ? {} : { groupKey: item.groupKey }),
        /* WHAT MAKES IT LOOK LIKE TEAMS RATHER THAN A BARE BANNER. The shell
           renders these into a Windows toast document: a circular sender
           photograph it downloads and caches itself, and the workspace/account
           line under the message. Only the workspace knows any of it, and only
           `showPreview` decides whether a face may be shown at all — a hidden
           preview that still displays the sender's portrait is not hidden. */
        ...(options.showPreview && item.preview.actorAvatarUrl !== undefined
          ? { avatarUrl: item.preview.actorAvatarUrl }
          : {}),
        ...(attribution.workspace === '' ? {} : { workspace: attribution.workspace }),
        ...(attribution.account === '' ? {} : { account: attribution.account }),
        urgent: item.priority === 'urgent',
        silent: true,
      })
      .catch(() => undefined);
    return;
  }

  if (!desktopSupported() || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible') return;

  const body = options.showPreview ? (item.preview.snippet ?? item.body ?? '') : 'New activity';
  const shared = {
    body,
    tag: item.groupKey ?? item.scopeId ?? item.id,
    requireInteraction: item.priority === 'urgent',
    /* The in-app chime already played; never double up. */
    silent: true,
    /* See sw.js: '/favicon.svg' never existed, so this silently fell back to
       the browser default. PNG because platforms do not reliably rasterise a
       vector notification icon. */
    icon: '/brand/cyphex-teams-192.png',
    /* Carried so BOTH sides can find this again: the worker's stale sweep and
       `dismissDesktopNotificationsForScope` read `scopeId`, and the worker's
       click handler reads `deepLink`. */
    data: { id: item.id, deepLink: item.deepLink ?? '/', scopeId: item.scopeId ?? null },
  };

  /*
   * RAISE IT THROUGH THE SERVICE WORKER WHEN THERE IS ONE.
   *
   * `requireInteraction` keeps urgent banners on screen until a human dismisses
   * them, so something has to be able to take them back. Only the worker can:
   * a banner created here with `new Notification()` is owned by this page and
   * is not returned by `registration.getNotifications()`, which is exactly why
   * probe notifications from days earlier were still sitting in the OS shade
   * with no code able to reach them.
   *
   * The fallback below is for browsers with no worker at all; there the local
   * handle is the only retraction available, and it dies with the page.
   */
  void workerRegistration()
    .then((registration) => (registration === null ? null : registration.showNotification(item.title, shared)))
    .catch(() => undefined);

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) return;

  try {
    const banner = new Notification(item.title, shared);
    /*
     * A RAISED BANNER HAS TO BE RETRACTABLE.
     *
     * `requireInteraction` is true for urgent items, and an incoming call is
     * urgent — so the OS keeps the banner up until somebody dismisses it BY
     * HAND. Nothing closed it when the ring ended, so a call you had already
     * answered, declined or missed stayed in the notification centre and came
     * back every time the shade was opened. Holding the handle is what lets
     * `callPopupActions.cancel` — the single exit every ring passes through —
     * take it away when the call is over.
     */
    live.set(item.id, { banner, scopeId: item.scopeId ?? null });
    banner.onclose = () => live.delete(item.id);
    banner.onclick = () => {
      window.focus();
      live.delete(item.id);
      banner.close();
      options.onActivate(item);
    };
  } catch {
    /* Notification construction throws on some platforms (e.g. Android w/o SW). */
  }
}
